import { Injectable, signal } from '@angular/core';
import { ContentArea } from '../../../../../core/models/invoice/invoice-document-detail.model';
import { BaseInvoiceThemeModel } from '../../../../../core/models/invoice/base-invoice-theme.model';
import {
  SectionId,
  SectionType,
  ThemeTypes,
} from '../../../../../features/invoice-theme/theme.types';
import { InvoicePositionModel } from '../../../../../core/models/finance/financial-record.model';
import { KeyValueRecord, recordToKvArray } from '../../../../../shared/functions/record-functions';

@Injectable()
export class InvoiceStateService {
  invoiceId = signal<number>(-1);
  theme = signal<ThemeTypes>(ThemeTypes.StandardInvoiceTheme);
  contentAreas = signal<Map<SectionId, ContentArea>>(new Map()); // Map that stores the section contents.
  themeProps = signal<BaseInvoiceThemeModel>(null);
  invoicePositions = signal<InvoicePositionModel[]>([]);
  meta = signal<KeyValueRecord[]>([]);

  /** * Content Area State methods ***/
  public updateContentArea(sectionId: SectionId, patch: Partial<ContentArea>): void {
    this.contentAreas.update((prev) => {
      // 1. Read the current entry or fall back to a default
      const current: ContentArea = prev.get(sectionId) ?? {
        type: SectionType.HTML,
        content: '',
        meta: {},
      };

      // 2. Create a new ContentArea by merging current with the patch
      const updated: ContentArea = {
        ...current,
        ...patch,
        meta: { ...(current.meta ?? {}), ...(patch.meta ?? {}) },
      };

      // 3. Create a new Map (clone) and set the updated entry
      const next = new Map(prev);
      next.set(sectionId, updated);

      // 4. Return the new Map — signal sees identity change
      return next;
    });
  }

  public replaceAllContentAreaData(map: Map<SectionId, ContentArea>): void {
    this.contentAreas.update(() => {
      return new Map(map);
    });
  }

  /** * Theme State methods ***/
  public updateTheme(prop: string, value: string): void {
    this.themeProps.update((theme) => {
      return {
        ...theme,
        [prop]: value,
      };
    });
  }

  public replaceThemeData(theme: BaseInvoiceThemeModel): void {
    this.themeProps.set(theme);
  }

  /** * Meta data * **/
  public updateMeta(value: Record<string, unknown>): void {
    this.meta.update((old) => {
      const updates = recordToKvArray(value);

      // Build a map of key → value, starting from old, then overwrite with new
      const merged = new Map(old.map((entry) => [entry.key, entry.value]));
      updates.forEach(({ key, value }) => merged.set(key, value));

      // Turn map back into array
      return Array.from(merged, ([key, value]) => ({ key, value }));
    });
  }

  public replaceMeta(data: KeyValueRecord[]): void {
    this.meta.set(data);
  }

  /** * Default methods ***/
  public reset(): void {
    this.invoiceId.set(-1);
    this.theme.set(ThemeTypes.StandardInvoiceTheme);
    this.contentAreas.set(new Map());
    this.themeProps.set({
      id: null,
      themeColor: '',
      footerBackgroundColor: '',
    });
    this.invoicePositions.set([]);
    this.meta.set([]);
  }
}
